import React from 'react';
import { Box, Button, Checkbox, FormControlLabel, FormGroup, Switch, TextField, Typography } from '@mui/material';
import InlineAlert from '../../../../shared/components/InlineAlert';

interface DatasetKeysSectionProps {
  requiredColumns: string[];
  /** Named optional columns: listed, but a key must be a required column. */
  optionalColumns: string[];
  /** Columns that look like the identity of a row (an Id or Arn path), offered as key in one click. */
  keySuggestions: { index: number; name: string }[];
  onUseAsKey: (index: number) => void;
  keyFields: string[];
  keyWarnings: string[];
  orderingField: string;
  keepCurrentState: boolean;
  locked: boolean;
  errors: Record<string, string>;
  onToggleKey: (column: string) => void;
  onOrderingChange: (column: string) => void;
  onKeepCurrentStateChange: (keep: boolean) => void;
}

/** What a row is about, which row for a key is newest, and whether a current state is kept. */
const DatasetKeysSection: React.FC<DatasetKeysSectionProps> = ({
  requiredColumns,
  optionalColumns,
  keySuggestions,
  onUseAsKey,
  keyFields,
  keyWarnings,
  orderingField,
  keepCurrentState,
  locked,
  errors,
  onToggleKey,
  onOrderingChange,
  onKeepCurrentStateChange,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <Box>
      <Typography variant="subtitle2">Key fields</Typography>
      <Typography variant="caption" color="text.secondary">
        The required columns that identify the thing a row is about.
      </Typography>
      <FormGroup row aria-label="Key fields">
        {requiredColumns.map((name) => (
          <FormControlLabel
            key={name}
            label={name}
            control={
              <Checkbox
                size="small"
                checked={keyFields.includes(name)}
                onChange={() => onToggleKey(name)}
                disabled={locked}
                inputProps={{ 'aria-label': `Key field ${name}` }}
              />
            }
          />
        ))}
        {optionalColumns.map((name) => (
          <FormControlLabel
            key={name}
            disabled
            label={
              <Box component="span" sx={{ display: 'inline-flex', flexDirection: 'column' }}>
                <span>{name}</span>
                <Typography variant="caption" color="text.disabled">Make required to use as key</Typography>
              </Box>
            }
            control={<Checkbox size="small" checked={false} inputProps={{ 'aria-label': `Key field ${name}` }} />}
          />
        ))}
      </FormGroup>
      {!locked && keyFields.length === 0 && keySuggestions.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
          {keySuggestions.map((s) => (
            <Button key={s.index} size="small" variant="outlined" onClick={() => onUseAsKey(s.index)}>
              Use {s.name} as key
            </Button>
          ))}
        </Box>
      )}
      {errors.keyFields && <Typography variant="caption" color="error">{errors.keyFields}</Typography>}
      {keyWarnings.length > 0 && (
        <InlineAlert severity="warning" sx={{ mt: 1 }}>
          {keyWarnings.join(', ')} changes with every event, so each event becomes its own key and the current
          state holds one row per event.
        </InlineAlert>
      )}
    </Box>

    <TextField
      select
      size="small"
      label="Ordering field"
      value={orderingField}
      onChange={(e) => onOrderingChange(e.target.value)}
      disabled={locked}
      error={!!errors.orderingField}
      helperText={errors.orderingField || 'Decides the newest row for a key'}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      sx={{ maxWidth: 320 }}
    >
      {requiredColumns.map((name) => <option key={name} value={name}>{name}</option>)}
    </TextField>

    <FormControlLabel
      label="Keep current state"
      control={
        <Switch
          size="small"
          checked={keepCurrentState}
          onChange={(e) => onKeepCurrentStateChange(e.target.checked)}
          disabled={locked}
        />
      }
    />
  </Box>
);

export default DatasetKeysSection;
